import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DateTime } from 'luxon';
import IconHome from '../../components/icons/IconHome';
import IconEdit from '../../components/icons/IconEdit';
import IconDone from '../../components/icons/IconDone';
import IconAdd from '../../components/icons/IconAdd';
import IconSave from '../../components/icons/IconSave';
import IconAddLink from '../../components/icons/IconAddLink';
import { createExerciseTemplate } from '../exercise-templates/api';
import { getLessonHomeworks, createLessonHomework, updateLessonHomework, deleteLessonHomework } from './lessonHomeworkApi';
import { getLessonTopics } from './workspace/lessonTopicsApi';
import * as coursesApi from '../courses/api';
import language from '../../variables/language';
import '../../css/lesson-topic-link.css';
import '../../css/lesson-detail-modern.css';

const emptyForm = () => ({ text: '', exercise: null });

// New, visually separate block in LessonDetailModal.jsx (right below "Слова
// заняття") - several homework entries per lesson, each free text plus an
// optional link to ONE ExerciseTemplate. Mirrors TopicElementList.jsx's own
// "pick existing OR create new, then attach" pattern (the only precedent
// for this exact need elsewhere in the app) for the exercise side, and the
// lesson<->topic flyout's own pill/search-list/popup visual language
// (lesson-topic-link.css) for full consistency with the rest of this
// modal, rather than pulling in ExerciseTemplatePicker's different
// participant-search-* look.
//
// The exercise picker is a floating popup (.lesson-topic-flyout, absolute-
// positioned), NOT inline content inside the form - an earlier version
// rendered the search input/list directly in the form flow, which grew and
// shrank the card's own height as the picker opened/closed, visibly
// shifting everything below it. A popup overlays instead of reflowing.
//
// Only ONE row is ever editable at a time (editingId: null | 'new' | a
// real id) - clicking a different row (or Add) swaps the form to it,
// discarding any unsaved edit on the previous one, same simplicity
// tradeoff already made for the top-level lesson form's own dirty state.
export default function LessonHomeworkPanel({ lessonId, onClose, lessonDate, lessonStart }) {
   const navigate = useNavigate();
   const location = useLocation();
   const lang = language[language.lang];

   const [homeworks, setHomeworks] = useState([]);
   // A homework exercise is only meaningful once it has a real place in a
   // course (see persist()/handleQuickCreateExercise below, which attach it
   // to this lesson's own topic) - there's nowhere to attach it to without
   // one, so adding homework is blocked until a topic exists.
   const [linkedTopics, setLinkedTopics] = useState([]);
   const [editingId, setEditingId] = useState(null);
   const [form, setForm] = useState(emptyForm());
   const [exerciseTemplates, setExerciseTemplates] = useState(null);
   const [exerciseQuery, setExerciseQuery] = useState('');
   const [exercisePickerOpen, setExercisePickerOpen] = useState(false);
   const [creatingExercise, setCreatingExercise] = useState(false);
   const [saving, setSaving] = useState(false);
   const exercisePickerRef = useRef(null);

   const loadHomeworks = () => {
      getLessonHomeworks(lessonId)
         .then(response => setHomeworks(Array.isArray(response.data) ? response.data : []))
         .catch(() => setHomeworks([]));
   };
   const loadLinkedTopics = () => {
      getLessonTopics(lessonId)
         .then(response => setLinkedTopics(Array.isArray(response.data) ? response.data : []))
         .catch(() => setLinkedTopics([]));
   };

   useEffect(() => {
      loadHomeworks();
      loadLinkedTopics();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [lessonId]);

   const hasTopic = linkedTopics.length > 0;

   useEffect(() => {
      const handleOutside = (e) => {
         if (exercisePickerRef.current && !exercisePickerRef.current.contains(e.target)) setExercisePickerOpen(false);
      };
      document.addEventListener('mousedown', handleOutside);
      return () => document.removeEventListener('mousedown', handleOutside);
   }, []);

   const ensureExerciseTemplatesLoaded = () => {
      if (exerciseTemplates !== null) return;
      coursesApi.getExerciseTemplates()
         .then(response => setExerciseTemplates(Array.isArray(response.data) ? response.data : []))
         .catch(() => setExerciseTemplates([]));
   };

   const startAdd = () => {
      if (!hasTopic) return;
      loadLinkedTopics(); // re-check freshness right before opening - a topic added via the header flyout moments ago must not still read as "none" here
      setForm(emptyForm());
      setExerciseQuery('');
      setExercisePickerOpen(false);
      setEditingId('new');
   };
   const startEdit = (hw) => {
      setForm({ text: hw.text || '', exercise: hw.exerciseTemplate || null });
      setExerciseQuery('');
      setExercisePickerOpen(false);
      setEditingId(hw.id);
   };
   const cancelEdit = () => {
      setEditingId(null);
      setForm(emptyForm());
      setExercisePickerOpen(false);
   };

   const goToExercise = (e, exerciseId) => {
      e.stopPropagation();
      onClose();
      navigate('/exercise-templates/' + exerciseId, { state: { returnTo: location.pathname } });
   };

   // A homework exercise only has a real "place" a course preview can jump
   // to once it's actually attached to a topic (TopicExerciseTemplate) -
   // otherwise it's just a floating ExerciseTemplate row with a name and
   // nothing else. Attaches to this lesson's own topic (guaranteed to exist
   // by the hasTopic gate above) so Events.jsx/GroupLessonsList.jsx's own
   // homework-chip link can send a teacher straight there instead of the
   // bare constructor. Idempotent server-side (TopicExerciseTemplateService.
   // create) so this is safe to call even if it's already attached.
   // Position: no cheap way to compute "true end of this topic's content"
   // from here (that aggregation lives in TopicElementList.jsx, course-
   // authoring only) - a high sentinel value keeps this from landing before
   // content someone deliberately ordered, at the cost of not necessarily
   // being the literal last slot.
   const attachExerciseToLessonTopic = (exerciseTemplateId) => {
      if (!exerciseTemplateId || linkedTopics.length === 0) return Promise.resolve();
      return coursesApi.createOne('/api/topicexercisetemplates', {
         topic: String(linkedTopics[0].topic.id),
         exerciseTemplate: String(exerciseTemplateId),
         position: 9999,
      }).catch(() => {});
   };

   // exerciseTemplateId explicit override lets handleCreateExercise persist
   // with a freshly-created id it just got back, without waiting for a
   // render + relying on stale `form.exercise` state.
   const persist = (exerciseTemplateId) => {
      const resolvedExerciseId = exerciseTemplateId !== undefined ? exerciseTemplateId : (form.exercise ? form.exercise.id : null);
      const payload = {
         lesson: lessonId,
         text: form.text,
         exerciseTemplate: resolvedExerciseId,
      };
      setSaving(true);
      const request = editingId === 'new' ? createLessonHomework(payload) : updateLessonHomework(editingId, payload);
      return request.then(() => attachExerciseToLessonTopic(resolvedExerciseId)).then(() => {
         loadHomeworks();
         cancelEdit();
      }).catch(() => {}).finally(() => setSaving(false));
   };

   const handleSave = () => {
      if (saving) return;
      persist();
   };

   const handleDelete = (e, id) => {
      e.stopPropagation();
      deleteLessonHomework(id).then(loadHomeworks).catch(() => {});
      if (editingId === id) cancelEdit();
   };

   // PUT here is a full replace (established backend convention) - text and
   // exerciseTemplate must be resent as-is or they'd be wiped to null, only
   // completed actually changes. Optimistic local flip first so the click
   // feels instant, loadHomeworks() afterwards reconciles with the server.
   const handleToggleComplete = (e, hw) => {
      e.stopPropagation();
      const nextCompleted = !hw.completed;
      setHomeworks(prev => prev.map(item => item.id === hw.id ? { ...item, completed: nextCompleted } : item));
      const payload = {
         lesson: lessonId,
         text: hw.text,
         exerciseTemplate: hw.exerciseTemplate ? hw.exerciseTemplate.id : null,
         completed: nextCompleted,
      };
      updateLessonHomework(hw.id, payload).then(loadHomeworks).catch(() => loadHomeworks());
   };

   // The exercise-creation step needs a real id back before this row can
   // reference it, so - unlike picking an EXISTING exercise (local state
   // only, saved on the next explicit "Зберегти") - this path saves
   // immediately, then leaves the lesson modal entirely for the new
   // exercise's own builder, same as TopicElementList.jsx already does.
   //
   // No intermediate "name this exercise" modal - a name is auto-derived
   // from the lesson's own date/time (falls back to "now" if the lesson
   // somehow has no date yet), since the teacher lands straight in the
   // builder and can rename it there in a second if they want something
   // else. Eliminates the extra click the older ExerciseTemplateCreateModal
   // step required.
   const defaultExerciseName = () => {
      const dt = lessonDate ? DateTime.fromFormat(lessonDate + ' ' + (lessonStart || '00:00'), 'yyyy-MM-dd HH:mm') : DateTime.now();
      const stamp = dt.isValid ? dt.toFormat('dd.MM.yyyy HH:mm') : DateTime.now().toFormat('dd.MM.yyyy HH:mm');
      return lang.lessonHomeworkExerciseDefaultName + ' ' + stamp;
   };

   const handleQuickCreateExercise = () => {
      if (creatingExercise) return;
      setExercisePickerOpen(false);
      setCreatingExercise(true);
      createExerciseTemplate({ name: defaultExerciseName() }).then((response) => {
         const newTemplate = response.data;
         const payload = { lesson: lessonId, text: form.text, exerciseTemplate: newTemplate.id };
         const request = editingId === 'new' ? createLessonHomework(payload) : updateLessonHomework(editingId, payload);
         return request.then(() => attachExerciseToLessonTopic(newTemplate.id)).then(() => {
            onClose();
            navigate('/exercise-templates/' + newTemplate.id, { state: { returnTo: location.pathname } });
         });
      }).catch(() => {}).finally(() => setCreatingExercise(false));
   };

   const openExercisePicker = () => {
      ensureExerciseTemplatesLoaded();
      setExerciseQuery('');
      setExercisePickerOpen(v => !v);
   };
   const pickExercise = (t) => {
      setForm(prev => ({ ...prev, exercise: t }));
      setExercisePickerOpen(false);
   };

   const filteredTemplates = (exerciseTemplates || []).filter(t => {
      if (!exerciseQuery.trim()) return true;
      return (t.name || '').toLowerCase().includes(exerciseQuery.trim().toLowerCase());
   });

   const renderForm = () => (
      <div className='lesson-homework-form' onClick={(e) => e.stopPropagation()}>
         <textarea className='lesson-content-textarea lesson-homework-textarea' placeholder={lang.lessonHomeworkTextPlaceholder}
            value={form.text} onChange={(e) => setForm(prev => ({ ...prev, text: e.target.value }))} />
         {form.exercise ? (
            <span className='lesson-topic-chip'>
               <span className='lesson-topic-chip-label'>{form.exercise.name}</span>
               <span className='lesson-topic-chip-remove' onClick={() => setForm(prev => ({ ...prev, exercise: null }))}>×</span>
            </span>
         ) : (
            <span className='lesson-topic-trigger-wrap' ref={exercisePickerRef}>
               <span className='lesson-homework-exercise-trigger' onClick={openExercisePicker}>
                  <IconAddLink style={{ fill: exercisePickerOpen ? 'var(--lighttext-color)' : 'var(--secondarytext-color)', width: 13, height: 13 }} />
                  {lang.lessonHomeworkSearchExercise}
               </span>
               {exercisePickerOpen ? (
                  <div className='lesson-topic-flyout open-left' onMouseDown={(e) => e.stopPropagation()}>
                     <p className='lesson-topic-flyout-title'>{lang.lessonHomeworkSearchExercise}</p>
                     <input className='lesson-topic-search-input' type='text' placeholder={lang.lessonHomeworkSearchExercise}
                        value={exerciseQuery} onChange={(e) => setExerciseQuery(e.target.value)} autoFocus />
                     <div className='lesson-topic-option-list'>
                        {exerciseTemplates === null ? (
                           <p className='lesson-topic-flyout-empty'>{lang['Loading ...']}</p>
                        ) : filteredTemplates.length === 0 ? (
                           <p className='lesson-topic-flyout-empty'>{lang['Empty list']}</p>
                        ) : filteredTemplates.map(t => (
                           <div key={t.id} className='lesson-topic-option' onClick={() => pickExercise(t)}>
                              {t.name}
                           </div>
                        ))}
                     </div>
                     <span className={'lesson-homework-create-exercise' + (creatingExercise ? ' disabled' : '')} onClick={handleQuickCreateExercise}>
                        <IconAdd style={{ width: 12, height: 12 }} />{creatingExercise ? lang['Loading ...'] : lang.lessonHomeworkCreateExercise}
                     </span>
                  </div>
               ) : ''}
            </span>
         )}
         <div className='student-card-form-footer lesson-homework-form-footer'>
            <div className='student-card-discard-link' onClick={cancelEdit}>{lang.cancel}</div>
            <button type='button' className='student-card-save-btn' disabled={saving} onClick={handleSave}>
               <IconSave />
               {saving ? lang['Loading ...'] : lang.save}
            </button>
         </div>
      </div>
   );

   return (
      <>
         <p className='student-card-section-title lesson-homework-title'>
            <span className='lesson-detail-section-title-icon'><IconHome /></span>
            {lang.lessonHomeworks}
         </p>
         <div className='lesson-homework-list'>
            {homeworks.map(hw => (
               editingId === hw.id ? (
                  <div className='lesson-homework-card editing' key={hw.id}>{renderForm()}</div>
               ) : (
                  <div className={'lesson-homework-card' + (hw.completed ? ' completed' : '')} key={hw.id} onClick={() => startEdit(hw)}>
                     <span className={'lesson-homework-card-check' + (hw.completed ? ' checked' : '')}
                        onClick={(e) => handleToggleComplete(e, hw)}
                        title={hw.completed ? lang.lessonHomeworkMarkIncomplete : lang.lessonHomeworkMarkComplete}>
                        <IconDone style={{ width: 16, height: 16 }} />
                     </span>
                     <div className='lesson-homework-card-body'>
                        {hw.text ? <p className='lesson-homework-card-text'>{hw.text}</p> : ''}
                        {hw.exerciseTemplate ? (
                           <span className='lesson-topic-chip lesson-homework-card-exercise'
                              onClick={(e) => goToExercise(e, hw.exerciseTemplate.id)}>
                              {hw.exerciseTemplate.name}
                           </span>
                        ) : ''}
                     </div>
                     <span className='lesson-homework-card-edit-hint'><IconEdit style={{ width: 12, height: 12 }} /></span>
                     <span className='lesson-topic-chip-remove lesson-homework-card-remove' onClick={(e) => handleDelete(e, hw.id)}>×</span>
                  </div>
               )
            ))}
            {homeworks.length === 0 && editingId !== 'new' ? <p className='lesson-topic-flyout-empty'>{lang.lessonHomeworksEmpty}</p> : ''}
            {editingId === 'new' ? (
               <div className='lesson-homework-card editing'>{renderForm()}</div>
            ) : hasTopic ? (
               <div className='lesson-homework-add-row' onClick={startAdd}>
                  <IconAdd style={{ width: 13, height: 13 }} />
                  <span>{lang.lessonHomeworkAdd}</span>
               </div>
            ) : (
               <div className='lesson-homework-add-row disabled' title={lang.lessonHomeworkNeedsTopic}>
                  <IconAdd style={{ width: 13, height: 13 }} />
                  <span>{lang.lessonHomeworkNeedsTopic}</span>
               </div>
            )}
         </div>
      </>
   );
}
